import dayjs from "dayjs";

import { addressLine } from "../../Login/lib/placeToAddress";
import { formatPatientDOB } from "../TreatmentPlans/component/shared/PatientInfoCard";
import { describeApiError } from "../TreatmentPlans/lib/apiError";
import { patientSearchParams } from "../TreatmentPlans/lib/patientSearch";

/**
 * Pure helpers for adding a patient from the request wizard (NewPatientForm), and
 * for showing who a patient is while picking one (PatientPicker). No React here,
 * so the rules are tested directly.
 */

/**
 * A date of birth for display — "Aug 30, 1986".
 *
 * Only the date part is read, the rule the Patient Info card already uses
 * (`formatPatientDOB`). DOBs are stored as midnight UTC, so handing dayjs the
 * whole timestamp shows the day BEFORE anywhere west of Greenwich — which is
 * every office we have.
 */
export function formatDob(birthDate) {
  if (!birthDate) return "";
  return dayjs(formatPatientDOB({ birth_date: birthDate })).format("MMM D, YYYY");
}

/** Digits only, without a leading North American "1": the server wants exactly 10. */
export function phoneDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits.length === 11 && digits.charAt(0) === "1" ? digits.slice(1) : digits;
}

// A search box is usually typed in lower case; the record shouldn't keep that.
// Anything already in mixed case ("McDonald", "de Souza") is left as typed.
function tidyName(text) {
  const t = String(text || "").trim().replace(/\s+/g, " ");
  if (!t || t !== t.toLowerCase()) return t;
  return t.replace(/(^|[\s'-])([a-z])/g, function (m, sep, ch) {
    return sep + ch.toUpperCase();
  });
}

/**
 * The search that found nobody, as the new-patient form's starting values: a
 * phone number, an email address, or a name — "First Last" or "Last, First". A
 * single word is taken as the first name; the form is there to correct it.
 */
export function prefillFromQuery(query) {
  const q = String(query || "").trim();
  if (!q) return {};
  if (/^[\d\s()+.-]+$/.test(q)) return { phone: phoneDigits(q) };
  if (patientSearchParams(q).email) return { email: q };
  const comma = q.indexOf(",");
  if (comma !== -1) {
    return { firstName: tidyName(q.slice(comma + 1)), lastName: tidyName(q.slice(0, comma)) };
  }
  const words = q.split(/\s+/);
  return { firstName: tidyName(words[0]), lastName: tidyName(words.slice(1).join(" ")) };
}

/** The search reads as a name, so the call to action can repeat it back. */
export function isNameQuery(query) {
  const p = prefillFromQuery(query);
  return !!(p.firstName || p.lastName);
}

/**
 * The form's values as the details `controller.createPatient` sends. Optional
 * fields left blank come back as "" and are left out of the request — the
 * server's phone, province and postal-code validators refuse an empty string.
 */
export function buildNewPatient(values) {
  const v = values || {};
  const text = function (x) { return String(x || "").trim(); };
  const region = text(v.state);
  return {
    firstName: text(v.first_name),
    lastName: text(v.last_name),
    birthDate: v.birth_date ? v.birth_date.format("YYYY-MM-DD") : "",
    phone: phoneDigits(v.phone),
    email: text(v.email),
    address: text(v.address),
    city: text(v.city),
    // "on" -> "ON", the form the PMS stores; a spelled-out name is kept as typed.
    state: region.length <= 3 ? region.toUpperCase() : region,
    zipCode: text(v.zip_code).toUpperCase(),
  };
}

/**
 * A place picked in the address search (placeToAddress's shape) as the form's
 * address fields. Every part is set, blank or not: a newly picked address
 * replaces the old one rather than inheriting its postal code.
 */
export function addressFromPlace(parsed) {
  const a = parsed || {};
  return {
    address: addressLine(a, 200),
    city: a.city || "",
    state: a.state_code || a.state || "",
    zip_code: a.postal_code || "",
  };
}

// What the server can reject field by field, under the names the form uses.
const FORM_FIELDS = [
  "first_name", "last_name", "birth_date", "phone", "email",
  "address", "city", "state", "zip_code",
];

const FALLBACK = "Couldn't add the patient. Please try again.";

/**
 * What a `createPatient` answer means for the form:
 *
 * - `{ kind: "created", patient }` — select them. Marked `from_pms: false`: they
 *   have no PMS record until its next sync links them, and step 2 says so.
 * - `{ kind: "matches", candidates }` — the office already has someone with these
 *   names and date of birth; offer them instead of a second record.
 * - `{ kind: "fields", fields }` — per-field rejections, as antd `setFields` rows.
 * - `{ kind: "error", message }` — anything else, said in words.
 */
export function readCreateResponse(res) {
  const body = (res && res.json) || {};
  const status = res && res.status;
  if (status >= 200 && status < 300 && body.id) {
    return { kind: "created", patient: Object.assign({}, body, { from_pms: false }) };
  }
  if (status === 409 && body.error_type === "possible_duplicates") {
    return { kind: "matches", candidates: body.candidates || [] };
  }
  const fields = FORM_FIELDS.filter(function (name) {
    return Array.isArray(body[name]) && body[name].length;
  }).map(function (name) {
    return { name, errors: body[name].map(String) };
  });
  if (status === 400 && fields.length) return { kind: "fields", fields };
  return { kind: "error", message: describeApiError(res, FALLBACK) };
}
